import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
} from '@mui/material'
import { Form, Formik } from 'formik'
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { boolean, object } from 'yup'

import { useUpdateDocuments } from '../../../hooks/api/documents/useUpdateDocuments'
import { getProtectedS3, putToS3 } from '../../../hooks/useS3Storage'
import { useAuthContext } from '../../auth/hooks/useAuthContext'
import DriverAgreement from './DriverAgreement'

interface DriverAgreementDocument {
  document?: {
    custom_name?: string
    id: number
  }
  document_url?: string
  file_key?: string
  name?: string
  status?: string
}
interface DriverAgreementModalProps {
  agreementData: DriverAgreementDocument | null
  isPending: boolean
  language: string
  onClose: () => void
  onSuccess?: () => Promise<void>
  open: boolean
}

const DriverAgreementModal: React.FC<DriverAgreementModalProps> = ({
  agreementData,
  isPending,
  language = 'en',
  onClose,
  onSuccess,
  open,
}) => {
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])
  const { t } = useTranslation()
  const { userInfo } = useAuthContext()
  const [isProcessing, setIsProcessing] = useState(false)
  const [driverName, setDriverName] = useState('')
  const { mutateAsync: updateDocument } = useUpdateDocuments()
  const [notification, setNotification] = useState({
    message: '',
    open: false,
    severity: 'success' as 'error' | 'success',
  })

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false })
  }

  const validationSchema = object({
    agreementAccepted: boolean()
      .oneOf([true], t('auth.register.driver_agreement_required'))
      .required(t('auth.register.driver_agreement_required')),
  })

  useEffect(() => {
    if (userInfo) {
      const fullName =
        `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim()
      setDriverName(fullName)
    }
  }, [userInfo])

  const getDocumentKeyByLanguage = (lang: string): string => {
    switch (lang) {
      case 'de':
        return 'fahrervereinbarung.pdf'
      case 'es':
        return 'acuerdo_conductor.pdf'
      default:
        return 'uride_driver_agreement_form.pdf'
    }
  }

  const modifyAndUploadAgreement = async (): Promise<string> => {
    try {
      setIsProcessing(true)
      const fileKey = getDocumentKeyByLanguage(language)

      const fileData = await getProtectedS3(`profile/${fileKey}`)
      if (!fileData || !fileData.href) {
        throw new Error('Could not retrieve agreement file')
      }
      const pdfBytes = await fetch(fileData.href).then((res) =>
        res.arrayBuffer()
      )
      const pdfDoc = await PDFDocument.load(pdfBytes)
      const form = pdfDoc.getForm()
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
      const today = new Date().toLocaleDateString()
      const pages = pdfDoc.getPages()
      const lastPage = pages[pages.length - 2]

      const getInitials = (name: string): string =>
        name
          .split(' ')
          .map((part) => part.charAt(0))
          .join('')
      const driverInitials = getInitials(driverName)

      try {
        if (
          form.getTextField(
            'understand and agree to the terms set  forth in this agreement'
          )
        )
          form
            .getTextField(
              'understand and agree to the terms set  forth in this agreement'
            )
            .setText(driverName)
        if (form.getTextField('undefined'))
          form.getTextField('undefined').setText(today)
        if (form.getTextField('1 Uride  Procedure  Manual  Initials'))
          form
            .getTextField('1 Uride  Procedure  Manual  Initials')
            .setText(driverInitials)
        if (
          form.getTextField(
            '2 Uride  Driver  Terms  and  Conditions   Initials'
          )
        )
          form
            .getTextField('2 Uride  Driver  Terms  and  Conditions   Initials')
            .setText(driverInitials)
        if (form.getTextField('Typed or Printed Name'))
          form.getTextField('Typed or Printed Name').setText(driverName)
        if (form.getTextField('Date')) form.getTextField('Date').setText(today)
        if (form.getTextField('Signature1_es_:signer:signature'))
          form
            .getTextField('Signature1_es_:signer:signature')
            .setText(driverName)
        form.flatten()

        lastPage.drawText(`Electronically signed by: ${driverName}`, {
          color: rgb(0, 0, 0),
          font,
          size: 12,
          x: 50,
          y: 50,
        })
        lastPage.drawText(`Date: ${today}`, {
          color: rgb(0, 0, 0),
          font,
          size: 12,
          x: 50,
          y: 30,
        })
      } catch (error) {
        console.warn('Could not fill the Driver Agreement form')
      }

      const modifiedBytes = await pdfDoc.save()
      const blob = new Blob([modifiedBytes], { type: 'application/pdf' })
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      const safeDriverName = driverName
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase()
      const signedFileName = `${safeDriverName}_signed_agreement_${timestamp}.pdf`
      const file = new File([blob], signedFileName, { type: 'application/pdf' })

      const fileUrl = await putToS3(
        file,
        agreementData?.name,
        agreementData?.name || signedFileName
      )

      return fileUrl
    } catch (error) {
      console.error('Error modifying and uploading PDF')
      throw error
    } finally {
      setIsProcessing(false)
    }
  }

  const handleSubmit = async (values) => {
    if (values.agreementAccepted) {
      try {
        const fileUrl = await modifyAndUploadAgreement()
        if (!live.current) return
        await updateDocument({
          custom_name: agreementData.document?.custom_name,
          document_id: agreementData.document.id,
          key1: fileUrl,
        })
        if (!live.current) return
        if (onSuccess) {
          await onSuccess()
        }
        onClose()
      } catch (error) {
        if (!live.current) return
        console.error('Error during agreement submission')
        setNotification({
          message: t('auth.registerdriver_agreement_error'),
          open: true,
          severity: 'error',
        })
      }
    }
  }

  return (
    <>
      <Dialog
        aria-labelledby="driver-agreement-dialog-title"
        fullWidth
        maxWidth="md"
        onClose={onClose}
        open={open}
      >
        <DialogTitle id="driver-agreement-dialog-title">
          {isPending
            ? t('auth.register.driver_agreement_title')
            : t('auth.register.driver_agreement_view')}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {isPending ? (
            <Formik
              initialValues={{
                agreementAccepted: false,
                required: true,
              }}
              onSubmit={handleSubmit}
              validationSchema={validationSchema}
            >
              {({ isSubmitting }) => (
                <Form>
                  <DriverAgreement language={language} />
                  <DialogActions>
                    <Button
                      disabled={isProcessing || isSubmitting}
                      onClick={onClose}
                    >
                      {t('common.close')}
                    </Button>
                    <Button
                      color="primary"
                      disabled={isProcessing || isSubmitting}
                      type="submit"
                      variant="contained"
                    >
                      {isProcessing ? (
                        <CircularProgress size={24} />
                      ) : (
                        t('auth.register.driver_agreement_to_sign')
                      )}
                    </Button>
                  </DialogActions>
                </Form>
              )}
            </Formik>
          ) : (
            <Box>
              <iframe
                height="500px"
                src={agreementData?.document_url || ''}
                title={t('auth.register.driver_agreement_title')}
                width="100%"
              />
              <DialogActions>
                <Button onClick={onClose}>{t('common.close')}</Button>
              </DialogActions>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        open={notification.open}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default DriverAgreementModal
